import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Backdrop, Box, CircularProgress, Container, Link, Snackbar, Typography } from '@mui/material';
import { Link as RouterLink, useLocation, useParams } from 'react-router-dom';
import { makeStyles } from '@mui/styles';

import { Layout } from 'features/common/components/Layout';
import { ShowAlertState } from 'features/common/types';
import { ErrorType, PageInternalError404 } from 'features/common/components/PageInternalError404';
import { extractErrorMessage, isNotFoundError } from 'features/quotation/helpers';
import { getProviderById } from 'features/providers/services';
import { IProviderDTO } from 'features/providers/types';
import ProviderDetail from 'features/providers/components/ProviderDetail';
import { getProviderDetailBackRoute, ProviderDetailLocationState } from 'features/providers/helpers/detailView';

export const useStyles = makeStyles((theme) => ({
	backdrop: {
		zIndex: theme.zIndex.modal + 1,
		color: '#fff',
	},
}));

export const ProviderDetailPage = (): JSX.Element => {
	const classes = useStyles();
	const { id } = useParams<{ id: string }>();
	const location = useLocation<ProviderDetailLocationState>();
	const [loading, setLoading] = useState(false);
	const defaultAlertState: ShowAlertState = { show: false, severity: 'success', message: '' };
	const [alert, setAlert] = useState<ShowAlertState>(defaultAlertState);
	const [provider, setProvider] = useState<IProviderDTO | null>(null);
	const [showError404, setShowError404] = useState(false);
	const [refreshKey, setRefreshKey] = useState(0);

	const loadProvider = useCallback(async () => {
		if (!id) return;
		try {
			setLoading(true);
			setShowError404(false);
			setProvider((await getProviderById(id)).data);
		} catch (error) {
			if (isNotFoundError(error)) {
				setShowError404(true);
			} else {
				setAlert({
					show: true,
					severity: 'error',
					message: extractErrorMessage(error, 'Ocurrió un error al recibir los detalles del proveedor'),
				});
			}
		} finally {
			setLoading(false);
		}
	}, [id]);

	useEffect(() => {
		loadProvider();
	}, [loadProvider, refreshKey]);

	const handleProviderUpdated = () => setRefreshKey((prev) => prev + 1);

	return (
		<>
			<Layout>
				<Container component="main" maxWidth="xl" sx={{ py: 4 }}>
					<Backdrop className={classes.backdrop} open={loading}>
						<CircularProgress color="inherit" />
					</Backdrop>

					<Box sx={{ mb: 3 }}>
						<Link
							component={RouterLink}
							to={getProviderDetailBackRoute(location.state)}
							underline="hover"
							variant="body2"
						>
							← Volver a proveedores
						</Link>
						<Typography variant="h5" sx={{ fontWeight: 600, mt: 1 }}>
							Detalle de proveedor
						</Typography>
					</Box>

					{provider && <ProviderDetail provider={provider} onProviderUpdated={handleProviderUpdated} />}
					{showError404 && <PageInternalError404 errorType={ErrorType.INTERNAL_AUTHORIZATION_NOT_FUND} />}
				</Container>
			</Layout>

			<Snackbar open={alert.show} autoHideDuration={3000} onClose={() => setAlert(defaultAlertState)}>
				<Alert variant="filled" severity={alert.severity}>
					{alert.message}
				</Alert>
			</Snackbar>
		</>
	);
};
